import type { ResumeData } from "@izicvs/schema";

// Signature defaults applied when a template is selected. They preserve each
// template's identity while staying fully editable in the customisation panel
// (theme colours + typography). `text`/`background`/`font` are optional.
export type TemplateDefaults = {
  primary: string;
  text?: string;
  background?: string;
  font?: { family: string; variants: string[] };
};

export const TEMPLATE_DEFAULTS: Record<string, TemplateDefaults> = {
  aurora: {
    primary: "#0f766e",
    font: { family: "Manrope", variants: ["regular", "500", "600", "700"] },
  },
  halo: {
    primary: "#334155",
    font: { family: "Rubik", variants: ["regular", "500", "600", "700"] },
  },
  david: {
    primary: "#0055a4",
    font: { family: "Work Sans", variants: ["regular", "500", "600", "700"] },
  },
  darcia: {
    primary: "#b76e79",
    font: { family: "Poppins", variants: ["regular", "500", "600", "700"] },
  },
  lumen: {
    primary: "#4f46e5",
    font: { family: "Sora", variants: ["regular", "500", "600", "700"] },
  },
  poncia: {
    primary: "#fb7185",
    font: { family: "Nunito", variants: ["regular", "500", "600", "700"] },
  },
  nissy: {
    primary: "#1c1c1c",
    text: "#101010",
    background: "#f8f8f8",
    font: { family: "Montserrat", variants: ["regular", "700", "800"] },
  },
  glad: { primary: "#f0e04b", text: "#101010", background: "#f8f8f8" },
  cobalt: {
    primary: "#3e6af2",
    text: "#212121",
    background: "#ffffff",
    font: { family: "IBM Plex Sans", variants: ["regular", "500", "600", "700"] },
  },
  atlas: {
    primary: "#3e6af2",
    text: "#212121",
    background: "#ffffff",
    font: { family: "IBM Plex Sans", variants: ["regular", "500", "600", "700"] },
  },
  noir: {
    primary: "#212121",
    text: "#212121",
    background: "#ffffff",
    font: { family: "Space Grotesk", variants: ["regular", "500", "700"] },
  },
  fredline: {
    primary: "#1a1a1a",
    text: "#1a1a1a",
    background: "#f6f6f6",
    font: { family: "Inter", variants: ["regular", "500", "600", "700"] },
  },
  junior: {
    primary: "#d67215",
    text: "#5c6168",
    background: "#ffffff",
    font: { family: "Inter", variants: ["regular", "500", "600", "700"] },
  },
};

// Return a new ResumeData with the template and its signature theme/font applied
// (pure; safe to use for a live preview or before persisting).
export const applyTemplate = (data: ResumeData, template: string): ResumeData => {
  const next: ResumeData = structuredClone(data);
  next.metadata.template = template;

  const defaults = TEMPLATE_DEFAULTS[template];
  if (!defaults) return next;

  next.metadata.theme.primary = defaults.primary;
  if (defaults.text) next.metadata.theme.text = defaults.text;
  if (defaults.background) next.metadata.theme.background = defaults.background;
  if (defaults.font) {
    next.metadata.typography.font.family = defaults.font.family;
    next.metadata.typography.font.subset = "latin";
    next.metadata.typography.font.variants = defaults.font.variants;
  }
  return next;
};
